import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { GoogleLogin } from '@react-oauth/google';
import axios from 'axios';
import { ShieldCheck, Sparkles, User as UserIcon, LogOut, ChevronDown, ChevronUp, X, FileText, CheckCircle2, UploadCloud, HelpCircle, Target, Award, AlertTriangle } from 'lucide-react';
import TemplateSelector from './components/TemplateSelector';
import MyResumes from './components/MyResumes';
import { StepGraphic } from './components/Illustrations';
import PricingModal from './components/PricingModal';
import PricingPage from './components/PricingPage';
import CheckoutPage from './components/CheckoutPage';
import PaymentSuccessPage from './components/PaymentSuccessPage';
import { API_BASE_URL, APP_NAME, SUPPORT_EMAIL } from './config';

export default function App() {
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('editor'); // 'editor' | 'my_resumes'
  const [currentPath, setCurrentPath] = useState(window.location.pathname || '/');
  const [searchParams, setSearchParams] = useState(new URLSearchParams(window.location.search));

  const navigate = (to, params = null) => {
    let url = to;
    if (params) {
      const sp = new URLSearchParams(params);
      url += `?${sp.toString()}`;
      setSearchParams(sp);
    } else {
      setSearchParams(new URLSearchParams());
    }
    window.history.pushState({}, '', url);
    setCurrentPath(to);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    const onPopState = () => {
      setCurrentPath(window.location.pathname);
      setSearchParams(new URLSearchParams(window.location.search));
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  // Sync initial URL path to auth modals if user visited /login or /signup directly
  useEffect(() => {
    const p = (window.location.pathname || '/').toLowerCase().replace(/\/$/, '') || '/';
    if (p === '/login' || p === '/signin') {
      setAuthMode('login');
      setShowAuthModal(true);
    } else if (p === '/signup' || p === '/register') {
      setAuthMode('signup');
      setShowAuthModal(true);
    }
  }, []);

  const cleanPath = (currentPath || '/').toLowerCase().replace(/\/$/, '') || '/';

  // Dynamic document title based on route for SEO & Google Ads Quality Score
  useEffect(() => {
    if (cleanPath === '/pricing' || cleanPath === '/plans') {
      document.title = 'Plans & Pricing - Fact-Locked AI Resume Tailoring | Cvify';
    } else if (cleanPath === '/checkout' || cleanPath === '/pay') {
      document.title = 'Secure Checkout - Review Your Order | Cvify';
    } else if (cleanPath === '/payment-success' || cleanPath === '/thank-you') {
      document.title = 'Order Confirmed - Thank You for Your Purchase | Cvify';
    } else if (cleanPath === '/my-resumes' || cleanPath === '/dashboard') {
      document.title = 'My Resumes - Saved & Optimized CVs | Cvify';
    } else if (cleanPath === '/templates') {
      document.title = 'Choose ATS Resume Template | Cvify';
    } else if (cleanPath === '/login' || cleanPath === '/signin') {
      document.title = 'Sign In to Your Account | Cvify';
    } else if (cleanPath === '/signup' || cleanPath === '/register') {
      document.title = 'Create an Account | Cvify';
    } else {
      document.title = 'AI Resume Builder & Free ATS Resume Checker | Cvify';
    }
  }, [cleanPath]);

  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('signup'); // 'signup' | 'login'
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [jd, setJd] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [docError, setDocError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [paidSubmissionId, setPaidSubmissionId] = useState(null);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const resultsRef = useRef(null);
  const profileDropdownRef = useRef(null);

  // Close profile dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    if (dropdownOpen) {
      document.addEventListener('pointerdown', handleClickOutside);
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }
    return () => {
      document.removeEventListener('pointerdown', handleClickOutside);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [dropdownOpen]);

  // Active 799 plan check: user has Gold Plan (or premium_799) with remaining quota
  const hasActive799Plan = Boolean(
    user && 
    (user.active_plan === 'Gold Plan' || user.plan_type === 'premium_799') && 
    (user.resumes_left > 0)
  );

  // Form states for signup
  const [signupForm, setSignupForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    mobile_number: '',
    password: '',
  });

  // Form states for login (supports either email or mobile number)
  const [loginForm, setLoginForm] = useState({
    login: '',
    password: '',
  });

  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);

  // Restore user session on refresh
  useEffect(() => {
    const fetchUser = async () => {
      const token = localStorage.getItem('access_token');
      if (token) {
        try {
          const res = await axios.get(`${API_BASE_URL}/api/auth/me`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          setUser(res.data.user);
          localStorage.setItem('app_user', JSON.stringify(res.data.user));
        } catch (e) {
          console.error('Session invalid', e);
          const savedUser = localStorage.getItem('app_user');
          if (savedUser) setUser(JSON.parse(savedUser));
        }
      } else {
        const savedUser = localStorage.getItem('app_user');
        if (savedUser) setUser(JSON.parse(savedUser));
      }
    };
    fetchUser();
  }, []);

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      const res = await axios.post(`${API_BASE_URL}/api/auth/google`, {
        credential: credentialResponse.credential,
      });
      setUser(res.data.user);
      localStorage.setItem('app_user', JSON.stringify(res.data.user));
      if (res.data.access_token) {
        localStorage.setItem('access_token', res.data.access_token);
      }
      if (res.data.refresh_token) {
        localStorage.setItem('refresh_token', res.data.refresh_token);
      }
      setShowAuthModal(false);
    } catch (err) {
      alert('Authentication failed with backend');
    }
  };

  const handleSignupSubmit = async (e) => {
    e.preventDefault();
    setAuthError('');
    setAuthLoading(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/auth/signup`, signupForm);
      if (res.data.status === 'success') {
        const userObj = res.data.user;
        setUser(userObj);
        localStorage.setItem('app_user', JSON.stringify(userObj));
        if (res.data.access_token) {
          localStorage.setItem('access_token', res.data.access_token);
        }
        if (res.data.refresh_token) {
          localStorage.setItem('refresh_token', res.data.refresh_token);
        }
        setShowAuthModal(false);
        setSignupForm({ first_name: '', last_name: '', email: '', mobile_number: '', password: '' });
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Signup failed. Please verify your details.';
      setAuthError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setAuthError('');
    setAuthLoading(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/auth/login`, loginForm);
      if (res.data.status === 'success') {
        const userObj = res.data.user;
        setUser(userObj);
        localStorage.setItem('app_user', JSON.stringify(userObj));
        if (res.data.access_token) {
          localStorage.setItem('access_token', res.data.access_token);
        }
        if (res.data.refresh_token) {
          localStorage.setItem('refresh_token', res.data.refresh_token);
        }
        setShowAuthModal(false);
        setLoginForm({ login: '', password: '' });
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Invalid email or password.';
      setAuthError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('app_user');
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setUser(null);
    setShowTemplateSelector(false);
    setPaidSubmissionId(null);
    setActiveTab('editor');
    navigate('/');
  };

  // ATS Analysis Submission
  const handleAnalyze = async (e) => {
    e.preventDefault();
    if (!file || !jd) return alert('Please upload a PDF resume and provide a JD');

    const formData = new FormData();
    formData.append('resume', file);
    formData.append('job_description', jd);

    setLoading(true);
    setDocError(null);
    setAnalysis(null);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/analyze`, formData);
      setAnalysis(res.data);
      
      // Sync fresh user state & quota if logged in
      const token = localStorage.getItem('access_token');
      if (token) {
        try {
          const userRes = await axios.get(`${API_BASE_URL}/api/auth/me`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (userRes.data?.user) {
            setUser(userRes.data.user);
            localStorage.setItem('app_user', JSON.stringify(userRes.data.user));
          }
        } catch (_) {}
      }

      // Trigger smooth scroll (timeout ensures DOM has updated with the new analysis)
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.response?.data?.message || 'Analysis error: Unable to analyze the uploaded document.';
      setDocError(errorMsg);
      setAnalysis(null);
    } finally {
      setLoading(false);
    }
  };

  // Purchase trigger with Gate
  const initiatePaymentGateway = async (planType = 'single_49') => {
    if (!user) {
      // Intercept: prompt user to sign in first
      setShowAuthModal(true);
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      const authHeaders = token ? { 'Authorization': `Bearer ${token}` } : {};
      const subId = analysis?.submission_id || 'direct_sub';

      // 1. Ask backend to create Razorpay Order
      const orderRes = await fetch(`${API_BASE_URL}/api/create-razorpay-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ submission_id: subId, user_id: user.user_id || user.id, plan_type: planType })
      });

      const orderData = await orderRes.json();
      if (!orderRes.ok || orderData.status !== 'success') {
        // Extract the real error detail from FastAPI's HTTPException response
        const errMsg = orderData.detail || orderData.error || "Failed to initiate payment";
        alert(`Payment Error: ${errMsg}`);
        console.error("Order creation failed:", orderData);
        return;
      }

      // 2. Open Razorpay Modal
      const options = {
        key: orderData.key_id || process.env.REACT_APP_RAZORPAY_KEY_ID, 
        amount: orderData.amount,
        currency: orderData.currency || "INR",
        name: APP_NAME,
        description: planType === 'premium_799' ? "20-Resume Premium Monthly Quota" : "Unlock Tailored ATS Resume",
        order_id: orderData.order_id,
        modal: {
          ondismiss: function () {
            console.log("Razorpay checkout modal was closed by the user.");
          }
        },
        handler: async function (response) {
          // 3. Send successful payment details to backend for verification & upgrade
          try {
            const upgradeRes = await fetch(`${API_BASE_URL}/api/upgrade`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...authHeaders },
              body: JSON.stringify({
                submission_id: subId,
                user_id: user.user_id || user.id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature,
                plan_type: planType
              })
            });
            
            const upgradeData = await upgradeRes.json();
            if (upgradeData.status === 'success') {
              if (upgradeData.user) {
                setUser(upgradeData.user);
                localStorage.setItem('app_user', JSON.stringify(upgradeData.user));
              } else {
                try {
                  const userRes = await axios.get(`${API_BASE_URL}/api/auth/me`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                  });
                  if (userRes.data?.user) {
                    setUser(userRes.data.user);
                    localStorage.setItem('app_user', JSON.stringify(userRes.data.user));
                  }
                } catch (_) {}
              }
              // If there was an active analysis, store submission id
              if (upgradeData.submission_id && upgradeData.submission_id !== 'direct_sub') {
                setPaidSubmissionId(upgradeData.submission_id);
              }
              navigate('/payment-success', {
                plan: planType,
                payment_id: response.razorpay_payment_id,
                order_id: response.razorpay_order_id,
                sub_id: upgradeData.submission_id || subId
              });
            } else {
              const errMsg = upgradeData.detail || upgradeData.error || "Payment verification failed.";
              alert(`Payment verification error: ${errMsg}`);
            }
          } catch (error) {
            console.error("Payment verification request failed:", error);
            alert("Error communicating with verification server. Please contact support.");
          }
        },
        prefill: {
          name: (user.name || `${user.first_name || ''} ${user.last_name || ''}`).trim() || undefined,
          email: user.email || undefined,
          contact: user.mobile_number || undefined
        },
        theme: { color: "#4F46E5" }
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', function (response){
          console.error("Razorpay payment failed:", response.error);
          const reason = response.error?.description || response.error?.reason || "Transaction failed";
          alert(`Payment failed! Reason: ${reason}`);
      });
      rzp.open();
      
    } catch (err) {
      console.error("Checkout initiation error:", err);
      alert(`Could not initiate payment: ${err.message || "Network error"}`);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col font-sans relative overflow-x-hidden selection:bg-indigo-500 selection:text-white">
      {/* Subtle Ambient Background Mesh & Gradient Orbs */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden -z-10" aria-hidden="true">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[700px] h-[500px] bg-indigo-600/15 rounded-full blur-[130px] transform-gpu" />
        <div className="absolute top-40 right-[-10%] w-[500px] h-[400px] bg-sky-500/10 rounded-full blur-[120px] transform-gpu" />
        <div className="absolute bottom-20 left-[-10%] w-[550px] h-[450px] bg-purple-600/10 rounded-full blur-[140px] transform-gpu" />
        <div 
          className="absolute inset-0 opacity-[0.035]" 
          style={{ 
            backgroundImage: 'radial-gradient(circle at 1px 1px, #ffffff 1px, transparent 0)', 
            backgroundSize: '28px 28px' 
          }} 
        />
      </div>

      {/* Global Dismiss Backdrop for Profile Menu */}
      {dropdownOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black/25 backdrop-blur-[1px]"
          onClick={() => setDropdownOpen(false)}
          onTouchStart={() => setDropdownOpen(false)}
        />
      )}

      {/* Top Navigation Bar */}
      <header className="relative z-50 border-b border-slate-800 bg-slate-900/90 backdrop-blur px-3 sm:px-6 py-2.5 sm:py-3.5 flex items-center justify-between gap-2 sm:gap-4">
        {/* Brand Logo */}
        <div 
          onClick={() => navigate('/')} 
          className="flex items-center gap-2 sm:gap-2.5 cursor-pointer hover:opacity-90 transition flex-shrink-0"
        >
          <img 
            src="/images/logo-icon-light.png" 
            alt="Cvify Logo" 
            className="w-7 h-7 sm:w-8 sm:h-8 object-contain"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <span className="font-extrabold text-lg sm:text-xl tracking-tight bg-gradient-to-r from-indigo-400 via-sky-300 to-indigo-200 bg-clip-text text-transparent">{APP_NAME}</span>
        </div>

        {/* Desktop Navigation Tabs (Visible on Tablet & Desktop >= md) */}
        <div className="hidden md:flex items-center gap-1 bg-slate-950/80 p-1 rounded-xl border border-slate-800">
          <button
            onClick={() => navigate('/')}
            className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-semibold transition ${
              cleanPath === '/' || cleanPath === '/tailor'
                ? 'bg-indigo-600 text-white shadow-sm'
                : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            <Sparkles className="w-3.5 h-3.5" />
            <span>Tailor Resume</span>
          </button>
          <button
            onClick={() => {
              if (!user) {
                setAuthMode('login');
                setShowAuthModal(true);
                navigate('/login');
              } else {
                navigate('/my-resumes');
              }
            }}
            className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-semibold transition ${
              cleanPath === '/my-resumes'
                ? 'bg-indigo-600 text-white shadow-sm'
                : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            <FileText className="w-3.5 h-3.5" />
            <span>My Resumes</span>
          </button>
        </div>

        {/* User Status / Plans / Login Controls (Always fully visible and aligned) */}
        <div className="flex items-center gap-1.5 sm:gap-3 flex-shrink-0">
          {user ? (
            <div className="flex items-center gap-1.5 sm:gap-2.5">
              {!hasActive799Plan && (
                <motion.button 
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  transition={{ duration: 0.2 }}
                  onClick={() => navigate('/pricing')}
                  className={`flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs font-semibold transition shadow-sm cursor-pointer shrink-0 ${
                    cleanPath === '/pricing'
                      ? 'bg-indigo-600 text-white border border-indigo-400'
                      : 'bg-gradient-to-r from-amber-500/15 to-indigo-500/15 border border-amber-500/40 text-amber-300 hover:text-amber-200 hover:border-amber-300'
                  }`}
                >
                  <Sparkles className="w-3.5 h-3.5 text-amber-400" />
                  <span className="hidden sm:inline">Plans &amp; Pricing</span>
                  <span className="sm:hidden">Plans</span>
                  <span className="text-[9px] sm:text-[10px] bg-amber-400 text-slate-950 font-bold px-1.5 py-0.5 rounded-full">65% OFF</span>
                </motion.button>
              )}
              {user.active_plan && (
                <div className="hidden lg:flex items-center gap-1.5 bg-indigo-900/40 border border-indigo-500/30 px-3 py-1 rounded-full text-xs shrink-0">
                  <span>{user.active_plan === 'Gold Plan' ? '🥇' : '🥉'}</span>
                  <span className="font-semibold text-indigo-300">{user.active_plan}</span>
                  {user.active_plan === 'Gold Plan' && (
                    <span className="text-slate-400 ml-1">({user.resumes_left}/20 Left)</span>
                  )}
                </div>
              )}

              {/* Profile Dropdown Wrapper */}
              <div ref={profileDropdownRef} className="relative shrink-0">
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    setDropdownOpen((prev) => !prev);
                  }} 
                  className={`relative z-50 flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full border transition cursor-pointer select-none shrink-0 ${
                    dropdownOpen 
                      ? 'bg-slate-700/90 border-indigo-400 text-white ring-2 ring-indigo-500/40 shadow-sm' 
                      : 'bg-slate-800/80 hover:bg-slate-700/80 border-slate-700 text-slate-200'
                  }`}
                  aria-label="Profile Menu"
                  aria-expanded={dropdownOpen}
                >
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="w-6 h-6 sm:w-7 sm:h-7 rounded-full object-cover" />
                  ) : (
                    <UserIcon className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-400" />
                  )}
                  <span className="text-xs sm:text-sm font-medium hidden md:inline max-w-[120px] truncate">{user.name || user.email}</span>
                  <ChevronDown className={`w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-400 transition-transform duration-200 ${dropdownOpen ? 'rotate-180 text-indigo-400' : ''}`} />
                </button>

                {dropdownOpen && (
                  <div 
                    className="absolute right-0 top-full mt-2.5 w-[calc(100vw-1.5rem)] max-w-[280px] sm:max-w-xs sm:w-80 bg-slate-800 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden z-50"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="p-3.5 sm:p-4 border-b border-slate-700 space-y-1">
                      <p className="text-[10px] sm:text-xs font-semibold text-slate-400 uppercase tracking-wider">Account</p>
                      <p className="text-sm font-medium text-slate-100 truncate">{user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'User'}</p>
                      <p className="text-xs text-slate-400 truncate">{user.email}</p>
                      {user.mobile_number && (
                        <p className="text-xs text-slate-400">📱 {user.mobile_number}</p>
                      )}
                      <div className="pt-1.5">
                        <p className="text-[11px] text-slate-400 font-mono">
                          User ID: <span className="text-indigo-400 select-all">{user.user_id || user.id}</span>
                        </p>
                      </div>
                    </div>
                    <div className="p-1.5 sm:p-2 border-b border-slate-700">
                      <button 
                        onClick={() => {
                          navigate('/my-resumes');
                          setDropdownOpen(false);
                        }} 
                        className="w-full text-left px-3 py-2 text-xs sm:text-sm text-slate-200 hover:bg-slate-700 rounded-lg flex items-center gap-2 transition cursor-pointer"
                      >
                        <FileText className="w-4 h-4 text-indigo-400" />
                        My Resumes
                      </button>
                    </div>
                    <div className="p-3.5 sm:p-4 border-b border-slate-700">
                      <p className="text-[10px] sm:text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Support</p>
                      <p className="text-xs sm:text-sm">Email: <a href={`mailto:${SUPPORT_EMAIL}`} className="text-indigo-400 hover:underline">{SUPPORT_EMAIL}</a></p>
                      <p className="text-xs sm:text-sm mt-1.5">WhatsApp: <span className="text-slate-300 font-medium">+918877966766</span></p>
                      <p className="text-[11px] text-slate-400 mt-1">You can connect using WhatsApp 24*7</p>
                    </div>
                    <div className="p-1.5 sm:p-2">
                      <button 
                        onClick={() => {
                          handleLogout();
                          setDropdownOpen(false);
                        }} 
                        className="w-full text-left px-3 py-2 text-xs sm:text-sm text-rose-400 hover:bg-slate-700 rounded-lg flex items-center gap-2 transition cursor-pointer"
                      >
                        <LogOut className="w-4 h-4" />
                        Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-1 sm:gap-2">
              <motion.button 
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                transition={{ duration: 0.2 }}
                onClick={() => navigate('/pricing')}
                className={`flex items-center gap-1 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-[11px] sm:text-xs font-semibold transition shadow-sm cursor-pointer shrink-0 ${
                  cleanPath === '/pricing'
                    ? 'bg-indigo-600 text-white border border-indigo-400'
                    : 'bg-gradient-to-r from-amber-500/15 to-indigo-500/15 border border-amber-500/40 text-amber-300 hover:text-amber-200 hover:border-amber-300'
                }`}
              >
                <Sparkles className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-amber-400" />
                <span className="hidden sm:inline">Plans &amp; Pricing</span>
                <span className="sm:hidden">Plans</span>
                <span className="text-[9px] sm:text-[10px] bg-amber-400 text-slate-950 font-bold px-1.5 py-0.5 rounded-full">65% OFF</span>
              </motion.button>
              <motion.button 
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                transition={{ duration: 0.2 }}
                onClick={() => { setAuthMode('login'); setAuthError(''); setShowAuthModal(true); navigate('/login'); }}
                className="text-xs sm:text-sm font-medium text-slate-300 hover:text-white px-2.5 sm:px-3.5 py-1 sm:py-1.5 rounded-full border border-slate-700 hover:border-slate-600 transition cursor-pointer shrink-0"
              >
                Sign In
              </motion.button>
              <motion.button 
                whileHover={{ scale: 1.03, boxShadow: '0 8px 20px -4px rgba(99, 102, 241, 0.45)' }}
                whileTap={{ scale: 0.97 }}
                transition={{ duration: 0.2 }}
                onClick={() => { setAuthMode('signup'); setAuthError(''); setShowAuthModal(true); navigate('/signup'); }}
                className="hidden sm:inline-flex text-xs sm:text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-500 px-3 sm:px-4 py-1 sm:py-1.5 rounded-full transition shadow-md shadow-indigo-600/20 cursor-pointer shrink-0"
              >
                Sign Up
              </motion.button>
            </div>
          )}
        </div>
      </header>

      {/* Mobile Sub-Navigation Bar (< md) */}
      <nav className="md:hidden border-b border-slate-800/80 bg-slate-950/90 backdrop-blur px-3 py-1.5 flex items-center justify-center sticky top-0 z-40">
        <div className="grid grid-cols-2 w-full max-w-sm gap-1 bg-slate-900/90 p-1 rounded-xl border border-slate-800">
          <button
            onClick={() => navigate('/')}
            className={`flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-semibold transition ${
              cleanPath === '/' || cleanPath === '/tailor'
                ? 'bg-indigo-600 text-white shadow-sm'
                : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            <Sparkles className="w-3.5 h-3.5" />
            <span>Tailor Resume</span>
          </button>
          <button
            onClick={() => {
              if (!user) {
                setAuthMode('login');
                setShowAuthModal(true);
                navigate('/login');
              } else {
                navigate('/my-resumes');
              }
            }}
            className={`flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-semibold transition ${
              cleanPath === '/my-resumes'
                ? 'bg-indigo-600 text-white shadow-sm'
                : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            <FileText className="w-3.5 h-3.5" />
            <span>My Resumes</span>
          </button>
        </div>
      </nav>

      {/* Main Container with Page Transitions */}
      <AnimatePresence mode="wait">
        {cleanPath === '/my-resumes' || cleanPath === '/dashboard' || activeTab === 'my_resumes' ? (
          <motion.div
            key="my_resumes"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="w-full flex-1"
          >
            <MyResumes onBackToAnalyzer={() => { navigate('/'); setShowTemplateSelector(false); }} />
          </motion.div>
        ) : cleanPath === '/pricing' || cleanPath === '/plans' ? (
          <motion.div
            key="pricing_page"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="w-full flex-1"
          >
            <PricingPage
              user={user}
              hasActiveAnalysis={Boolean(analysis?.submission_id)}
              onNavigate={navigate}
              onSelectPlan={(plan) => navigate('/checkout', { plan })}
              onRequireLogin={() => { setAuthMode('login'); setAuthError(''); setShowAuthModal(true); navigate('/login'); }}
            />
          </motion.div>
        ) : cleanPath === '/checkout' || cleanPath === '/pay' ? (
          <motion.div
            key="checkout_page"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="w-full flex-1"
          >
            <CheckoutPage
              user={user}
              hasActiveAnalysis={Boolean(analysis?.submission_id)}
              initialPlan={searchParams.get('plan') || 'single_49'}
              onNavigate={navigate}
              onInitiatePayment={(plan) => initiatePaymentGateway(plan)}
              onRequireLogin={() => { setAuthMode('login'); setAuthError(''); setShowAuthModal(true); navigate('/login'); }}
            />
          </motion.div>
        ) : cleanPath === '/payment-success' || cleanPath === '/thank-you' ? (
          <motion.div
            key="payment_success_page"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="w-full flex-1"
          >
            <PaymentSuccessPage
              user={user}
              onNavigate={navigate}
              orderId={searchParams.get('order_id')}
              paymentId={searchParams.get('payment_id')}
              planType={searchParams.get('plan') || 'premium_799'}
              submissionId={searchParams.get('sub_id') || paidSubmissionId}
            />
          </motion.div>
        ) : (showTemplateSelector && paidSubmissionId) || cleanPath === '/templates' ? (
          <motion.div
            key="template_selector"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="w-full flex-1"
          >
            <TemplateSelector 
              submissionId={paidSubmissionId || searchParams.get('sub_id')} 
              userId={user?.user_id || user?.id}
              onFinished={() => navigate('/my-resumes')}
            />
          </motion.div>
        ) : (
          <motion.main
            key="editor"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="flex-1 max-w-5xl mx-auto w-full p-3.5 sm:p-6 space-y-6 sm:space-y-8"
          >
            {/* Hero Section with High-Authority SEO Copy */}
            <div className="text-center space-y-3 sm:space-y-4 pt-2 sm:pt-4 pb-2">
              <div className="inline-flex items-center gap-1.5 sm:gap-2 px-3 py-1 sm:px-3.5 sm:py-1.5 rounded-full text-[11px] sm:text-xs font-semibold bg-indigo-500/10 text-indigo-300 border border-indigo-500/20 backdrop-blur-sm">
                <Sparkles className="w-3.5 h-3.5 text-indigo-400" />
                <span>#1 AI Resume Builder &amp; Free ATS Checker</span>
              </div>
              
              <h1 className="text-2xl sm:text-4xl md:text-5xl font-black tracking-tight text-white max-w-3xl mx-auto leading-tight">
                The AI <span className="bg-gradient-to-r from-indigo-400 via-sky-400 to-indigo-300 bg-clip-text text-transparent">Resume Builder</span> &amp; Free <span className="bg-gradient-to-r from-indigo-400 via-sky-400 to-indigo-300 bg-clip-text text-transparent">ATS Checker</span>
              </h1>
              
              <p className="text-slate-300 text-xs sm:text-sm md:text-base max-w-2xl mx-auto leading-relaxed font-normal">
                <strong className="text-white font-semibold">Cvify</strong> is an advanced AI resume builder and free ATS checker engineered to help job seekers pass applicant tracking systems with a 98% interview rate. In seconds, our intelligent resume tailor analyzes your CV against any job description, pinpointing critical keyword gaps, optimizing action verbs, and scoring your ATS compatibility in real time.
              </p>
              
              <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 text-[11px] sm:text-xs font-medium text-slate-300 pt-1">
                <div className="flex items-center gap-1.5 bg-slate-900/60 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full border border-slate-800">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                  <span>98% ATS Pass Rate</span>
                </div>
                <div className="flex items-center gap-1.5 bg-slate-900/60 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full border border-slate-800">
                  <ShieldCheck className="w-3.5 h-3.5 text-indigo-400" />
                  <span>Fact-Locked Precision</span>
                </div>
                <div className="flex items-center gap-1.5 bg-slate-900/60 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full border border-slate-800">
                  <Sparkles className="w-3.5 h-3.5 text-sky-400" />
                  <span>Single-Page Auto-Fit</span>
                </div>
              </div>
            </div>

            {/* Onboarding / 4-Step Process Guide */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3 bg-slate-900/40 p-2.5 sm:p-3.5 rounded-2xl border border-slate-800/80 backdrop-blur-sm">
              <div className="flex items-center gap-2.5 sm:gap-3 p-2 sm:p-2.5 rounded-xl bg-slate-900/70 border border-indigo-500/30 shadow-sm">
                <StepGraphic step={1} className="w-7 h-7 sm:w-8 sm:h-8 flex-shrink-0" />
                <div className="min-w-0">
                  <span className="block text-[9px] sm:text-[10px] font-bold uppercase tracking-wider text-indigo-400">Step 1</span>
                  <p className="text-[11px] sm:text-xs font-semibold text-slate-200 truncate">Upload Resume</p>
                </div>
              </div>

              <div className="flex items-center gap-2.5 sm:gap-3 p-2 sm:p-2.5 rounded-xl bg-slate-900/50 border border-slate-800">
                <StepGraphic step={2} className="w-7 h-7 sm:w-8 sm:h-8 flex-shrink-0" />
                <div className="min-w-0">
                  <span className="block text-[9px] sm:text-[10px] font-bold uppercase tracking-wider text-indigo-400">Step 2</span>
                  <p className="text-[11px] sm:text-xs font-semibold text-slate-200 truncate">Paste Job Spec</p>
                </div>
              </div>

              <div className="flex items-center gap-2.5 sm:gap-3 p-2 sm:p-2.5 rounded-xl bg-slate-900/50 border border-slate-800">
                <StepGraphic step={3} className="w-7 h-7 sm:w-8 sm:h-8 flex-shrink-0" />
                <div className="min-w-0">
                  <span className="block text-[9px] sm:text-[10px] font-bold uppercase tracking-wider text-slate-400">Step 3</span>
                  <p className="text-[11px] sm:text-xs font-semibold text-slate-300 truncate">Pick Template</p>
                </div>
              </div>

              <div className="flex items-center gap-2.5 sm:gap-3 p-2 sm:p-2.5 rounded-xl bg-slate-900/50 border border-slate-800">
                <StepGraphic step={4} className="w-7 h-7 sm:w-8 sm:h-8 flex-shrink-0" />
                <div className="min-w-0">
                  <span className="block text-[9px] sm:text-[10px] font-bold uppercase tracking-wider text-slate-400">Step 4</span>
                  <p className="text-[11px] sm:text-xs font-semibold text-slate-300 truncate">Download PDF</p>
                </div>
              </div>
            </div>

            {/* Analysis & Upload Form */}
            <form onSubmit={handleAnalyze} className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5 bg-slate-900/60 p-4 sm:p-6 rounded-2xl border border-slate-800 backdrop-blur-sm shadow-xl">
              <div>
                <label className="block text-sm font-semibold mb-2 text-slate-200 flex items-center justify-between">
                  <span>Upload Resume (PDF)</span>
                  <span className="text-[11px] font-normal text-slate-400">Max 10MB</span>
                </label>
                <div className="relative border-2 border-dashed border-slate-700 hover:border-indigo-500/60 rounded-xl p-4 transition bg-slate-950/60 text-center flex flex-col items-center justify-center min-h-[120px]">
                  <UploadCloud className="w-8 h-8 text-indigo-400 mb-2" />
                  <p className="text-xs text-slate-300 font-medium">
                    {file ? (
                      <span className="text-emerald-400 font-semibold truncate max-w-[200px] block">
                        ✓ {file.name}
                      </span>
                    ) : (
                      "Click to choose file or drag PDF here"
                    )}
                  </p>
                  <p className="text-[11px] text-slate-500 mt-1">Accepts standard PDF documents</p>
                  <input 
                    type="file" 
                    accept=".pdf"
                    onChange={(e) => {
                      setFile(e.target.files[0]);
                      setDocError(null);
                      setAnalysis(null);
                    }} 
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2 text-slate-200 flex items-center justify-between">
                  <span>Job Description</span>
                  <span className="text-[11px] font-normal text-slate-400">Target Role Spec</span>
                </label>
                <textarea 
                  rows={4} 
                  value={jd} 
                  onChange={(e) => setJd(e.target.value)} 
                  placeholder="Paste Target Job Description, requirements, or responsibilities here..." 
                  className="w-full bg-slate-950/80 border border-slate-800 rounded-xl p-3 text-xs sm:text-sm focus:outline-none focus:border-indigo-500 min-h-[120px] resize-none text-slate-200 placeholder:text-slate-600 transition"
                />
              </div>

              {/* Document Validation Error Alert Banner */}
              <AnimatePresence>
                {docError && (
                  <motion.div 
                    initial={{ opacity: 0, y: -6, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -6, scale: 0.98 }}
                    transition={{ duration: 0.2 }}
                    className="md:col-span-2 overflow-hidden"
                  >
                    <div className="p-4 bg-rose-500/10 border border-rose-500/40 rounded-xl flex items-start gap-3.5 text-rose-200 shadow-lg shadow-rose-950/20">
                      <AlertTriangle className="w-5 h-5 text-rose-400 shrink-0 mt-0.5" />
                      <div className="text-xs sm:text-sm space-y-1">
                        <p className="font-bold text-rose-300">Invalid Document Detected</p>
                        <p className="text-slate-300 leading-relaxed">{docError}</p>
                        <p className="text-rose-400 font-semibold pt-0.5">Please upload your resume (CV) in PDF format to run ATS analysis.</p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <motion.button 
                type="submit" 
                disabled={loading}
                whileHover={{ scale: 1.02, boxShadow: '0 10px 25px -4px rgba(99, 102, 241, 0.4)' }}
                whileTap={{ scale: 0.98 }}
                transition={{ duration: 0.2 }}
                className="md:col-span-2 py-3.5 bg-indigo-600 hover:bg-indigo-500 rounded-xl font-bold transition text-white shadow-lg shadow-indigo-600/20 text-sm flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Sparkles className="w-4 h-4 animate-spin text-indigo-300" />
                    <span>Analyzing Alignment & Keywords...</span>
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    <span>Run Free ATS Analysis</span>
                  </>
                )}
              </motion.button>
            </form>

            {/* ATS Diagnostic Scorecard */}
            <AnimatePresence>
              {analysis && (
                <motion.div 
                  ref={resultsRef}
                  initial={{ opacity: 0, y: 16, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.98 }}
                  transition={{ duration: 0.3, ease: 'easeOut' }}
                  className="bg-slate-900 border border-slate-800 rounded-2xl p-4 sm:p-6 space-y-4 sm:space-y-6"
                >
                  <div className="flex items-center justify-between border-b border-slate-800 pb-3 sm:pb-4">
                    <div>
                      <h3 className="text-lg sm:text-xl font-bold">Analysis Results</h3>
                      <p className="text-xs sm:text-sm text-slate-400">Current Score: <span className="text-rose-400 font-bold">{analysis.current_ats_score}/100</span></p>
                    </div>
                    <div className="text-right">
                      <span className="text-[10px] sm:text-xs uppercase tracking-wider text-emerald-400 font-semibold">Target Match</span>
                      <p className="text-xl sm:text-2xl font-black text-emerald-400">{analysis.potential_ats_score}/100</p>
                    </div>
                  </div>

                  {/* Plan / Payment CTA */}
                  <div className={`border rounded-xl p-4 sm:p-5 flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4 transition-all ${
                    user?.active_plan && user?.resumes_left > 0
                      ? 'bg-emerald-950/30 border-emerald-500/40 shadow-lg shadow-emerald-950/20'
                      : 'bg-indigo-950/40 border-indigo-500/30'
                  }`}>
                    <div>
                      {user?.active_plan && user?.resumes_left > 0 ? (
                        <>
                          <h4 className="font-bold flex items-center gap-2 text-emerald-300 text-sm sm:text-base">
                            <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-400" />
                            Active Plan: {user.active_plan === 'Gold Plan' ? 'Premium Monthly (₹799)' : user.active_plan}
                          </h4>
                          <p className="text-xs text-slate-300 mt-1">
                            <span className="font-semibold text-emerald-400">{user.resumes_left} / {user.total_resume_quota || 20} resumes remaining</span>
                            {user.valid_until && ` • Valid until ${new Date(user.valid_until).toLocaleDateString()}`}
                            <span className="text-slate-400 block sm:inline sm:ml-2">(No extra payment needed — quota active)</span>
                          </p>
                        </>
                      ) : user?.active_plan && user?.resumes_left <= 0 ? (
                        <>
                          <h4 className="font-bold flex items-center gap-2 text-amber-300 text-sm sm:text-base">
                            <ShieldCheck className="w-4 h-4 sm:w-5 sm:h-5 text-amber-400" /> Monthly Quota Exhausted
                          </h4>
                          <p className="text-xs text-slate-400 mt-1">
                            You have used all 20 resumes in your plan. Renew your monthly plan or pay per resume below.
                          </p>
                        </>
                      ) : (
                        <>
                          <h4 className="font-bold flex items-center gap-2 text-indigo-300 text-sm sm:text-base">
                            <ShieldCheck className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-400" /> Choose Your Plan
                          </h4>
                          <p className="text-xs text-slate-400 mt-1">Unlock fact-locked tailored PDFs.</p>
                        </>
                      )}
                    </div>

                    <div className="flex flex-col sm:flex-row gap-2.5 sm:gap-3 items-stretch sm:items-center w-full md:w-auto">
                      {user?.active_plan && user?.resumes_left > 0 ? (
                        /* When plan is active (resumes left > 0 & not expired): ONLY Show Use Quota button. Payment options are hidden! */
                        <motion.button 
                          onClick={async () => {
                            try {
                              const token = localStorage.getItem('access_token');
                              const res = await fetch(`${API_BASE_URL}/api/use-quota`, {
                                method: 'POST',
                                headers: { 
                                  'Content-Type': 'application/json',
                                  ...(token && { 'Authorization': `Bearer ${token}` })
                                },
                                body: JSON.stringify({ submission_id: analysis.submission_id, user_id: user.user_id || user.id })
                              });
                              const data = await res.json();
                              if (res.ok && data.status === 'success') {
                                if (data.user) {
                                  setUser(data.user);
                                  localStorage.setItem('app_user', JSON.stringify(data.user));
                                } else {
                                  const userRes = await axios.get(`${API_BASE_URL}/api/auth/me`, {
                                    headers: { 'Authorization': `Bearer ${token}` }
                                  });
                                  if (userRes.data?.user) {
                                    setUser(userRes.data.user);
                                    localStorage.setItem('app_user', JSON.stringify(userRes.data.user));
                                  }
                                }
                                setPaidSubmissionId(data.submission_id);
                                setShowTemplateSelector(true);
                              } else {
                                alert(data.detail || "Error using quota.");
                              }
                            } catch (e) {
                              alert("Network error bypassing payment.");
                            }
                          }}
                          whileHover={{ scale: 1.03, boxShadow: '0 12px 25px -4px rgba(16, 185, 129, 0.45)' }}
                          whileTap={{ scale: 0.98 }}
                          transition={{ duration: 0.2 }}
                          className="w-full sm:w-auto px-6 py-3 bg-emerald-600 hover:bg-emerald-500 font-bold rounded-xl shadow-lg transition whitespace-nowrap text-white flex items-center justify-center gap-2 cursor-pointer"
                        >
                          <Sparkles className="w-4 h-4" /> Generate PDF (Use Quota — {user.resumes_left} Left)
                        </motion.button>
                      ) : (
                        /* Only show payment buttons when quota is 0, expired, or user has no active plan */
                        <>
                          <motion.button 
                            onClick={() => initiatePaymentGateway('single_49')} 
                            whileHover={{ scale: 1.03, boxShadow: '0 12px 25px -4px rgba(99, 102, 241, 0.45)' }}
                            whileTap={{ scale: 0.98 }}
                            transition={{ duration: 0.2 }}
                            className="w-full sm:w-auto px-4 py-2.5 bg-slate-800 border border-slate-700 hover:bg-slate-700 font-bold rounded-xl shadow-lg transition text-white flex flex-col items-center justify-center cursor-pointer"
                          >
                            <div className="flex items-center gap-1.5">
                              <span className="text-xs sm:text-sm font-bold">Pay Per Resume: ₹49</span>
                              <span className="text-[11px] sm:text-xs text-slate-400 line-through font-normal">₹149</span>
                            </div>
                            <span className="text-[10px] text-emerald-400 font-semibold mt-0.5">Save ₹100 • 67% OFF</span>
                          </motion.button>
                          <motion.button 
                            onClick={() => initiatePaymentGateway('premium_799')} 
                            whileHover={{ scale: 1.03, boxShadow: '0 12px 25px -4px rgba(99, 102, 241, 0.45)' }}
                            whileTap={{ scale: 0.98 }}
                            transition={{ duration: 0.2 }}
                            className="w-full sm:w-auto px-5 py-2.5 bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-500 hover:to-indigo-400 font-bold rounded-xl shadow-lg transition text-white flex flex-col items-center justify-center border border-indigo-400/40 cursor-pointer"
                          >
                            <div className="flex items-center gap-1.5">
                              <span className="text-xs sm:text-sm font-bold">Premium Monthly: ₹799</span>
                              <span className="text-[11px] sm:text-xs text-indigo-200/70 line-through font-normal">₹2,299</span>
                            </div>
                            <div className="flex items-center gap-1.5 text-[10px] text-amber-300 font-semibold mt-0.5">
                              <span>20 Resumes (₹39.95/ea)</span>
                              <span>•</span>
                              <span>65% OFF</span>
                            </div>
                          </motion.button>
                        </>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* SEO Authority Feature Grid */}
            <section id="features" className="pt-8 pb-4 border-t border-slate-800/80 space-y-6">
              <div className="text-center space-y-2">
                <h2 className="text-xl sm:text-2xl md:text-3xl font-extrabold text-white">
                  Why Job Seekers Rely on <span className="bg-gradient-to-r from-indigo-400 to-sky-400 bg-clip-text text-transparent">Cvify</span>
                </h2>
                <p className="text-slate-400 text-xs sm:text-sm max-w-xl mx-auto">
                  Over 75% of resumes are discarded by Applicant Tracking Systems before a human recruiter sees them. Here is how our dual-engine architecture helps you rank #1.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 pt-2">
                <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-5 space-y-3 hover:border-indigo-500/40 transition group">
                  <div className="w-10 h-10 rounded-xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-indigo-400 group-hover:scale-110 transition">
                    <Target className="w-5 h-5" />
                  </div>
                  <h3 className="text-sm sm:text-base font-bold text-white">Precision ATS Keyword Matching</h3>
                  <p className="text-xs text-slate-400 leading-relaxed">
                    Instantly identify missing hard skills, critical qualifications, and industry buzzwords demanded by corporate ATS parsers like Workday and Greenhouse.
                  </p>
                </div>

                <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-5 space-y-3 hover:border-sky-500/40 transition group">
                  <div className="w-10 h-10 rounded-xl bg-sky-500/10 border border-sky-500/20 flex items-center justify-center text-sky-400 group-hover:scale-110 transition">
                    <ShieldCheck className="w-5 h-5" />
                  </div>
                  <h3 className="text-sm sm:text-base font-bold text-white">Fact-Locked Bullet Points</h3>
                  <p className="text-xs text-slate-400 leading-relaxed">
                    Say goodbye to AI hallucinations. Cvify reframes and quantifies your authentic achievements without inventing fake jobs or unverified degrees.
                  </p>
                </div>

                <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-5 space-y-3 hover:border-emerald-500/40 transition group">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400 group-hover:scale-110 transition">
                    <Award className="w-5 h-5" />
                  </div>
                  <h3 className="text-sm sm:text-base font-bold text-white">1-Page Recruiter-Grade PDF</h3>
                  <p className="text-xs text-slate-400 leading-relaxed">
                    Export high-resolution, perfectly formatted single-page PDFs engineered with proven typography, spacing, and 100% ATS readability standards.
                  </p>
                </div>
              </div>
            </section>

            {/* Interactive FAQ Section */}
            <section id="faq" className="pt-6 pb-4 border-t border-slate-800/80 space-y-6">
              <div className="text-center space-y-2">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-slate-900 text-slate-300 border border-slate-800">
                  <HelpCircle className="w-3.5 h-3.5 text-indigo-400" />
                  <span>Got Questions?</span>
                </div>
                <h2 className="text-xl sm:text-2xl md:text-3xl font-extrabold text-white">
                  Frequently Asked Questions
                </h2>
                <p className="text-slate-400 text-xs sm:text-sm max-w-lg mx-auto">
                  Everything you need to know about Cvify, applicant tracking systems, and our AI resume optimizer.
                </p>
              </div>

              <div className="space-y-3 max-w-3xl mx-auto">
                {[
                  {
                    q: "What is an ATS checker and how does Cvify test my resume?",
                    a: "An ATS (Applicant Tracking System) checker simulates the parsing engines used by enterprise employers (such as Workday, Taleo, Greenhouse, and Lever). Cvify conducts a multi-agent semantic evaluation, comparing your resume directly against the job description to calculate your match score (0-100%) and reveal missing technical or domain keywords."
                  },
                  {
                    q: "Why is Cvify the best AI resume builder for beating ATS filters?",
                    a: "Unlike generic builders that only rearrange layout, Cvify aligns your actual bullet points to the exact vocabulary of the role. It ensures optimal keyword density, removes unparseable graphics, and auto-calibrates spacing to guarantee a clean, single-page PDF with a 98% ATS pass potential."
                  },
                  {
                    q: "Is the Cvify ATS resume score checker free to use?",
                    a: "Yes! You can upload your PDF resume and paste any target job description for a 100% free instant score analysis, breakdown of matching and missing keywords, and role-fit diagnosis."
                  },
                  {
                    q: "How does Cvify prevent hallucinations or fake experience on my resume?",
                    a: "Cvify employs strict Fact-Locking constraints. Our AI is prohibited from inventing previous employers, titles, college credentials, or fake accomplishments. It highlights, reorganizes, and quantifies your authentic experience so you can speak to every word during an interview."
                  },
                  {
                    q: "What resume templates are most compatible with ATS parsers?",
                    a: "The most ATS-friendly resumes use clean headings, standard fonts, and avoid columns or graphics that confuse parsing software. Cvify provides pre-validated templates (Professional Blue, Standard Yellow, and Clean White) that are tested and confirmed to parse cleanly across all modern recruiting platforms."
                  }
                ].map((faq, idx) => {
                  const isOpen = expandedFaq === idx;
                  return (
                    <div 
                      key={idx} 
                      className="bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden transition"
                    >
                      <button
                        type="button"
                        onClick={() => setExpandedFaq(isOpen ? null : idx)}
                        className="w-full text-left px-4 sm:px-5 py-3.5 flex items-center justify-between gap-3 text-xs sm:text-sm font-semibold text-slate-200 hover:text-white transition"
                        aria-expanded={isOpen}
                      >
                        <span>{faq.q}</span>
                        {isOpen ? (
                          <ChevronUp className="w-4 h-4 text-indigo-400 shrink-0" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-slate-500 shrink-0" />
                        )}
                      </button>
                      {isOpen && (
                        <div className="px-4 sm:px-5 pb-4 pt-1 text-xs text-slate-400 leading-relaxed border-t border-slate-800/40">
                          {faq.a}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>

            {/* SEO Landing Page Footer */}
            <footer className="pt-8 pb-10 border-t border-slate-800/80 text-center space-y-4">
              <div className="flex items-center justify-center gap-2">
                <img 
                  src="/images/logo-icon-light.png" 
                  alt="Cvify Logo" 
                  className="w-5 h-5 object-contain"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <span className="font-extrabold text-base tracking-tight text-white">{APP_NAME}</span>
                <span className="text-slate-600">•</span>
                <span className="text-xs text-slate-400">AI Resume Builder &amp; Free ATS Checker</span>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-slate-400">
                <a href="#features" className="hover:text-indigo-400 transition">Features</a>
                <a href="#faq" className="hover:text-indigo-400 transition">FAQ</a>
                <button onClick={() => setShowPricingModal(true)} className="hover:text-indigo-400 transition">Pricing Plans</button>
                <a href={`mailto:${SUPPORT_EMAIL}`} className="hover:text-indigo-400 transition">Support: {SUPPORT_EMAIL}</a>
                <span className="text-slate-500">Phone: +918877966766</span>
              </div>
              <p className="text-[11px] text-slate-600">
                © {new Date().getFullYear()} Cvify. All rights reserved. Designed to help job seekers conquer Applicant Tracking Systems.
              </p>
            </footer>
          </motion.main>
        )}
      </AnimatePresence>

      {/* Auth Modal (Sign Up & Sign In) */}
      <AnimatePresence>
        {showAuthModal && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 bg-black/75 backdrop-blur-md flex items-center justify-center p-4"
          >
            <motion.div 
              initial={{ opacity: 0, scale: 0.94, y: 18 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 14 }}
              transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
              className="bg-slate-900 border border-slate-800 rounded-2xl p-6 max-w-md w-full relative shadow-2xl space-y-5"
            >
              <button 
                onClick={() => { setShowAuthModal(false); setAuthError(''); }}
                className="absolute top-4 right-4 text-slate-400 hover:text-slate-200 transition"
                aria-label="Close modal"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Header */}
              <div className="text-center space-y-1">
                <div className="w-10 h-10 bg-indigo-600/20 text-indigo-400 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Sparkles className="w-5 h-5" />
                </div>
                <h3 className="text-xl font-bold text-white">
                  {authMode === 'signup' ? 'Create Your Account' : 'Welcome Back'}
                </h3>
                <p className="text-xs text-slate-400">
                  {authMode === 'signup'
                    ? 'Sign up to generate and store your optimized resumes'
                    : 'Sign in to access your resumes and downloads'}
                </p>
              </div>

              {/* Tab Switcher */}
              <div className="flex bg-slate-950 p-1 rounded-xl border border-slate-800 text-xs font-semibold">
                <button
                  type="button"
                  onClick={() => { setAuthMode('signup'); setAuthError(''); }}
                  className={`flex-1 py-2 rounded-lg transition ${
                    authMode === 'signup'
                      ? 'bg-indigo-600 text-white shadow'
                      : 'text-slate-400 hover:text-slate-200'
                  }`}
                >
                  Sign Up
                </button>
                <button
                  type="button"
                  onClick={() => { setAuthMode('login'); setAuthError(''); }}
                  className={`flex-1 py-2 rounded-lg transition ${
                    authMode === 'login'
                      ? 'bg-indigo-600 text-white shadow'
                      : 'text-slate-400 hover:text-slate-200'
                  }`}
                >
                  Sign In
                </button>
              </div>

              {/* Error Message */}
              {authError && (
                <div className="p-3 bg-rose-500/10 border border-rose-500/30 rounded-xl text-rose-400 text-xs">
                  {authError}
                </div>
              )}

              {/* Form */}
              {authMode === 'signup' ? (
                <form onSubmit={handleSignupSubmit} className="space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs font-medium text-slate-300 mb-1">First Name</label>
                      <input
                        type="text"
                        required
                        placeholder="Utkarsh"
                        value={signupForm.first_name}
                        onChange={(e) => setSignupForm({ ...signupForm, first_name: e.target.value })}
                        className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-slate-200 focus:outline-none focus:border-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-300 mb-1">Last Name</label>
                      <input
                        type="text"
                        required
                        placeholder="Saurav"
                        value={signupForm.last_name}
                        onChange={(e) => setSignupForm({ ...signupForm, last_name: e.target.value })}
                        className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-slate-200 focus:outline-none focus:border-indigo-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-300 mb-1">Email</label>
                    <input
                      type="email"
                      required
                      placeholder="user@example.com"
                      value={signupForm.email}
                      onChange={(e) => setSignupForm({ ...signupForm, email: e.target.value })}
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-slate-200 focus:outline-none focus:border-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-300 mb-1">Mobile Number</label>
                    <input
                      type="tel"
                      required
                      placeholder="9876543210"
                      value={signupForm.mobile_number}
                      onChange={(e) => setSignupForm({ ...signupForm, mobile_number: e.target.value })}
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-slate-200 focus:outline-none focus:border-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-300 mb-1">Password</label>
                    <input
                      type="password"
                      required
                      minLength={6}
                      placeholder="••••••••"
                      value={signupForm.password}
                      onChange={(e) => setSignupForm({ ...signupForm, password: e.target.value })}
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-slate-200 focus:outline-none focus:border-indigo-500"
                    />
                  </div>

                  <motion.button
                    type="submit"
                    disabled={authLoading}
                    whileHover={{ scale: 1.025, boxShadow: '0 8px 20px -4px rgba(99, 102, 241, 0.4)' }}
                    whileTap={{ scale: 0.98 }}
                    transition={{ duration: 0.2 }}
                    className="w-full mt-2 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-xs font-semibold shadow-lg transition flex items-center justify-center gap-2"
                  >
                    {authLoading ? 'Creating Account...' : 'Sign Up'}
                  </motion.button>
                </form>
              ) : (
                <form onSubmit={handleLoginSubmit} className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-300 mb-1">Email or Mobile Number</label>
                    <input
                      type="text"
                      required
                      placeholder="user@example.com or 9876543210"
                      value={loginForm.login}
                      onChange={(e) => setLoginForm({ ...loginForm, login: e.target.value })}
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-slate-200 focus:outline-none focus:border-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-300 mb-1">Password</label>
                    <input
                      type="password"
                      required
                      placeholder="••••••••"
                      value={loginForm.password}
                      onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-slate-200 focus:outline-none focus:border-indigo-500"
                    />
                  </div>

                  <motion.button
                    type="submit"
                    disabled={authLoading}
                    whileHover={{ scale: 1.025, boxShadow: '0 8px 20px -4px rgba(99, 102, 241, 0.4)' }}
                    whileTap={{ scale: 0.98 }}
                    transition={{ duration: 0.2 }}
                    className="w-full mt-2 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-xs font-semibold shadow-lg transition flex items-center justify-center gap-2"
                  >
                    {authLoading ? 'Signing In...' : 'Sign In'}
                  </motion.button>
                </form>
              )}

              {/* Social Divider */}
              <div className="relative flex py-1 items-center">
                <div className="flex-grow border-t border-slate-800"></div>
                <span className="flex-shrink mx-3 text-[11px] text-slate-500">or continue with</span>
                <div className="flex-grow border-t border-slate-800"></div>
              </div>

              {/* Google OAuth Login */}
              <div className="flex justify-center">
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={() => setAuthError('Google sign in was unsuccessful.')}
                  shape="pill"
                  theme="filled_blue"
                  size="medium"
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Subscription Plans & Pricing Modal */}
      <PricingModal
        isOpen={showPricingModal}
        onClose={() => setShowPricingModal(false)}
        user={user}
        hasActive799Plan={hasActive799Plan}
        onSelectPlan={(planType) => initiatePaymentGateway(planType)}
        onRequireLogin={() => {
          setAuthMode('login');
          setAuthError('');
          setShowAuthModal(true);
        }}
        hasActiveAnalysis={Boolean(analysis?.submission_id)}
      />
    </div>
  );
}
